/**
 * Retry utility with exponential backoff and circuit breaker pattern.
 * 
 * Provides configurable retry logic for transient failures in API calls.
 */

export interface RetryOptions {
  /** Maximum number of retry attempts (default: 3) */
  maxAttempts?: number;
  /** Initial delay in milliseconds (default: 1000) */
  initialDelay?: number;
  /** Maximum delay in milliseconds (default: 30000) */
  maxDelay?: number;
  /** Multiplier for exponential backoff (default: 2) */
  backoffMultiplier?: number;
  /** Function to determine if error is retryable (default: all errors are retryable) */
  isRetryable?: (error: unknown) => boolean;
}

export interface CircuitBreakerState {
  failures: number;
  lastFailureTime: number;
  isOpen: boolean;
}

/**
 * Default retry options
 */
const DEFAULT_OPTIONS: Required<RetryOptions> = {
  maxAttempts: 3,
  initialDelay: 1000,
  maxDelay: 30000,
  backoffMultiplier: 2,
  isRetryable: () => true,
};

/**
 * Circuit breaker state (shared across all retries)
 */
let circuitBreaker: CircuitBreakerState = {
  failures: 0,
  lastFailureTime: 0,
  isOpen: false,
};

const CIRCUIT_BREAKER_THRESHOLD = 5; // Open circuit after 5 failures
const CIRCUIT_BREAKER_TIMEOUT = 60000; // 60 seconds before attempting reset

/**
 * Check if error is retryable (network errors, 5xx, 429)
 */
export function isRetryableError(error: unknown): boolean {
  if (error instanceof Error) {
    // Network errors are retryable
    if (error.message.includes('fetch') || error.message.includes('network')) {
      return true;
    }
  }

  // Check if error has status code
  if (typeof error === 'object' && error !== null && 'status' in error) {
    const status = (error as { status: number }).status;
    // Retry on 5xx server errors and 429 (Too Many Requests)
    return status >= 500 || status === 429;
  }

  return true; // Default to retryable
}

/**
 * Calculate delay for exponential backoff
 */
function calculateDelay(attempt: number, options: Required<RetryOptions>): number {
  const delay = options.initialDelay * Math.pow(options.backoffMultiplier, attempt - 1);
  return Math.min(delay, options.maxDelay);
}

/**
 * Check if circuit breaker should prevent retry
 */
function shouldOpenCircuit(): boolean {
  const now = Date.now();
  
  // Reset circuit breaker if timeout has passed
  if (circuitBreaker.isOpen && now - circuitBreaker.lastFailureTime > CIRCUIT_BREAKER_TIMEOUT) {
    circuitBreaker = {
      failures: 0,
      lastFailureTime: 0,
      isOpen: false,
    };
    return false;
  }

  // Open circuit if threshold reached
  if (circuitBreaker.failures >= CIRCUIT_BREAKER_THRESHOLD) {
    circuitBreaker.isOpen = true;
    circuitBreaker.lastFailureTime = now;
    return true;
  }

  return false;
}

/**
 * Record a failure for circuit breaker
 */
function recordFailure(): void {
  circuitBreaker.failures++;
  circuitBreaker.lastFailureTime = Date.now();
}

/**
 * Record a success for circuit breaker
 */
function recordSuccess(): void {
  circuitBreaker.failures = 0;
  circuitBreaker.isOpen = false;
}

/**
 * Retry a function with exponential backoff
 * 
 * @param fn Function to retry
 * @param options Retry options
 * @returns Promise that resolves with function result or rejects after all retries fail
 */
export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const opts: Required<RetryOptions> = {
    ...DEFAULT_OPTIONS,
    ...options,
    isRetryable: options.isRetryable || DEFAULT_OPTIONS.isRetryable,
  };

  // Check circuit breaker
  if (shouldOpenCircuit()) {
    throw new Error('Circuit breaker is open. Too many failures. Please try again later.');
  }

  let lastError: unknown;

  for (let attempt = 1; attempt <= opts.maxAttempts; attempt++) {
    try {
      const result = await fn();
      recordSuccess();
      return result;
    } catch (error) {
      lastError = error;

      // Check if error is retryable
      if (!opts.isRetryable(error)) {
        recordFailure();
        throw error;
      }

      // If last attempt, throw error
      if (attempt === opts.maxAttempts) {
        recordFailure();
        throw error;
      }

      // Calculate delay and wait
      const delay = calculateDelay(attempt, opts);
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  recordFailure();
  throw lastError;
}

/**
 * Reset circuit breaker (useful for testing)
 */
export function resetCircuitBreaker(): void {
  circuitBreaker = {
    failures: 0,
    lastFailureTime: 0,
    isOpen: false,
  };
}

